import { toValue, type MaybeRefOrGetter } from 'vue'
import { useQuery } from '@pinia/colada'
import { getPackages, type Package } from '@/api/lytAccountingClient'

/**
 * Stable, non-secret cache identity for setup package metadata.
 * Scoped by invoice + store only — never API keys or tokens.
 */
export function packageMetadataQueryKey(invoiceNumber: string, storeId: string) {
  return ['package-metadata', invoiceNumber, storeId] as const
}

export type PackageMetadataQueryParams = {
  invoiceNumber: MaybeRefOrGetter<string>
  storeId: MaybeRefOrGetter<string>
}

/**
 * Shared Pinia Colada read boundary for setup package metadata.
 * PackageSelection lists the full result; PackageLogin derives the
 * matching package from the same cache entry via route packageId.
 */
export function usePackageMetadataQuery(params: PackageMetadataQueryParams) {
  return useQuery<Package[]>({
    // Getter key keeps the entry reactive to route/query param changes.
    key: () =>
      packageMetadataQueryKey(
        toValue(params.invoiceNumber),
        toValue(params.storeId),
      ),
    query: () =>
      getPackages({
        invoiceNumber: toValue(params.invoiceNumber),
        storeId: toValue(params.storeId),
      }),
  })
}

/**
 * Case-insensitive package lookup used by the login handoff screen.
 */
export function findPackageById(
  packages: Package[] | null | undefined,
  packageId: string,
): Package | undefined {
  const needle = packageId.toLowerCase()
  return (packages ?? []).find((pkg) => String(pkg.id).toLowerCase() === needle)
}
